import { ThunkAction } from 'redux-thunk';
import { CommonRootState } from '../combineReducers';
import { RootActions } from '../combineActions';
import { sendError } from '../sendError';
import axios, { AxiosError } from 'axios';

const fetchProgramReviewsNotificationsCountRequest = (contractorId: string) =>
  ({
    type: 'FETCH_PROGRAM_REVIEWS_NOTIFICATIONS_COUNT_REQUEST',
    contractorId
  } as const);

const fetchProgramReviewsNotificationsCountSuccess = (contractorId: string, count: number) =>
  ({
    type: 'FETCH_PROGRAM_REVIEWS_NOTIFICATIONS_COUNT_SUCCESS',
    contractorId,
    count
  } as const);

const fetchProgramReviewsNotificationsCountFailure = (contractorId: string, error: AxiosError) => {
  sendError(error);
  return {
    type: 'FETCH_PROGRAM_REVIEWS_NOTIFICATIONS_COUNT_FAILURE',
    contractorId,
    error: error.message
  } as const;
};

export const fetchProgramReviewsNotificationsCountIfNeeded = (
  organizationId: string,
  contractorId: string
): ThunkAction<Promise<void>, CommonRootState, null, RootActions> => async (dispatch, getState) => {
  if (shouldFetchProgramReviewsNotificationsCount(getState(), contractorId)) {
    dispatch(fetchProgramReviewsNotificationsCount(organizationId, contractorId));
  }
};

const shouldFetchProgramReviewsNotificationsCount = (state: CommonRootState, contractorId: string) => {
  const programReviewsNotifications = (state as any).programReviewsNotifications;
  return !Object.keys(programReviewsNotifications || {}).includes(contractorId);
};

const fetchProgramReviewsNotificationsCount = (
  _organizationId: string,
  contractorId: string
): ThunkAction<Promise<void>, CommonRootState, null, RootActions> => async dispatch => {
  dispatch(fetchProgramReviewsNotificationsCountRequest(contractorId));

  try {
    const filter = `contractorId eq ${contractorId} and (status eq 'Accepted' or status eq 'Completable' or status eq 'CompletableNotApplicable' or status eq 'Incomplete')`;
    const listUrl = `/api/v3.01/safetyProgramRequirements?$filter=${filter.replace(
      / /g,
      '+'
    )}&$expand=safetyProgramRequirementValidation`;

    const listResponse = await axios.get(listUrl);
    const items: Array<any> = listResponse?.data?.value ?? listResponse?.data ?? [];

    const count = items.reduce((acc, item) => {
      // For "Accepted" status, check if needsReacceptance is true
      if (item.status === 'Accepted') {
        const v = item.safetyProgramRequirementValidation;
        const needsReacceptance = v && v.needsReacceptance === true;
        return acc + (needsReacceptance ? 1 : 0);
      }
      // For "Completable", "CompletableNotApplicable" and "Incomplete" statuses, always count them
      if (item.status === 'Completable' || item.status === 'CompletableNotApplicable' || item.status === 'Incomplete') {
        return acc + 1;
      }
      return acc;
    }, 0);

    dispatch(fetchProgramReviewsNotificationsCountSuccess(contractorId, count));
  } catch (error) {
    const axiosError = error as AxiosError;
    dispatch(fetchProgramReviewsNotificationsCountFailure(contractorId, axiosError));
  }
};

export type Actions =
  | ReturnType<typeof fetchProgramReviewsNotificationsCountRequest>
  | ReturnType<typeof fetchProgramReviewsNotificationsCountSuccess>
  | ReturnType<typeof fetchProgramReviewsNotificationsCountFailure>;
